// SAT Talep detay sayfasi (5 asamali akis).
// Rol + durum + olusturan/direct manager iliskisine gore aksiyonlar:
//   Olusturan: pending_supervisor veya pending_it iken "Iptal"
//   Direct manager (olusturanin manager_id'si): pending_supervisor iken
//     Onayla/Reddet
//   IT: pending_it iken "Duzenle ve Onayla" (modal) / Reddet
//   company_manager: pending_manager iken Onayla/Reddet
//   HR: pending_hr iken "Tamamla" (fatura + actual_price modali) / Reddet
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useI18n } from "../providers/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import { hasAnyRole } from "../utils/roles";
import { satRequestService } from "../services/satRequestService";
import StatusBadge from "../components/satRequest/StatusBadge";
import PriorityBadge from "../components/satRequest/PriorityBadge";
import RequestTimeline from "../components/satRequest/RequestTimeline";
import "../styles/SATRequest.css";
import { useConfirm } from "../providers/ConfirmProvider";

const formatDate = (d, lang) => {
  if (!d) return "—";
  const locale = lang === "tr" ? "tr-TR" : "en-US";
  return new Date(d).toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SATRequestDetailPage = () => {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(null); // "supervisor" | "it" | "manager" | "hr"
  const [rejectReason, setRejectReason] = useState("");
  const [itEditOpen, setItEditOpen] = useState(false);
  const [itEditForm, setItEditForm] = useState({
    note: "",
    item_url: "",
    quantity: "",
    unit_price: "",
  });
  const [hrOpen, setHrOpen] = useState(false);
  const [hrForm, setHrForm] = useState({
    invoice_url: "",
    actual_price: "",
    note: "",
    file: null,
    mode: "file",
  });
  const [modalErrors, setModalErrors] = useState([]);
  const [actionError, setActionError] = useState("");

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current; // eski istek yeni cevabi ezmesin
    setError("");
    try {
      const data = await satRequestService.getById(id);
      if (seq !== loadSeq.current) return;
      setRequest(data.request);
      setActivities(data.activities || []);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      const msg = err.response?.data?.msg || err.message || t("common.genericError");
      setError(msg);
      setActionError(msg);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // baska bir talebe gecildiginde eski icerik gosterilmesin
    setRequest(null);
    setActivities([]);
    setLoading(true);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handle = async (fn, confirmMsg) => {
    if (busy) return;
    setActionError("");
    if (confirmMsg && !(await confirm(confirmMsg, { danger: false }))) return;
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.msg || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onUploadDoc = (kind, file) => {
    if (!file) return;
    handle(() =>
      kind === "invoice"
        ? satRequestService.uploadInvoice(id, file)
        : satRequestService.uploadProforma(id, file)
    );
  };

  const submitReject = async () => {
    if (busy) return;
    const reason = rejectReason.trim();
    if (reason.length < 10) {
      setModalErrors([t("satRequest.fields.reasonPlaceholder")]);
      return;
    }
    setModalErrors([]);
    setBusy(true);
    try {
      const fn = {
        supervisor: () => satRequestService.supervisorReject(id, reason),
        it: () => satRequestService.itReject(id, reason),
        manager: () => satRequestService.managerReject(id, reason),
        hr: () => satRequestService.hrReject(id, reason),
      }[rejectOpen];
      await fn();
      setRejectOpen(null);
      setRejectReason("");
      await load();
    } catch (err) {
      setModalErrors([err.response?.data?.msg || err.message]);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !request)
    return (
      <div className="sat-detail">
        <div className="sat-empty">{t("common.loading")}</div>
      </div>
    );
  if (error && !request)
    return (
      <div className="sat-detail">
        <div className="sat-empty sat-empty--error" role="alert">
          {error}
        </div>
      </div>
    );
  if (!request) return null;


  const isCreator = user?.id === request.created_by;
  const isDirectManager =
    !isCreator && user?.id && user.id === request.created_by_manager_id;
  const isIT = hasAnyRole(user, ["it", "admin"]);
  const isManager = hasAnyRole(user, ["company_manager", "admin"]);
  const isHr = hasAnyRole(user, ["hr", "admin"]);

  const canSupervisorAction = isDirectManager && request.status === "pending_supervisor";
  // Rol-sahibi kendi talebini de isleyebilir (kucuk ekipte tek IT/yonetici/IK
  // kendi talebini ilerletebilsin diye !isCreator kosulu kaldirildi).
  const canItAction = isIT && request.status === "pending_it";
  const canManagerAction = isManager && request.status === "pending_manager";
  const canHrAction = isHr && request.status === "pending_hr";
  const canCancel =
    isCreator &&
    (request.status === "pending_supervisor" || request.status === "pending_it");
  // Teklif/fatura her aktif asamada eklenebilir (olusturan veya rol-sahibi).
  const canUploadDocs =
    (isCreator || isIT || isManager || isHr || isDirectManager) &&
    request.status !== "rejected" &&
    request.status !== "cancelled";

  const total = Number(request.quantity) * Number(request.unit_price);
  const openItEdit = () => {
    setItEditForm({
      note: "",
      item_url: request.item_url || "",
      quantity: request.quantity != null ? String(request.quantity) : "",
      unit_price: request.unit_price != null ? String(request.unit_price) : "",
    });
    setModalErrors([]);
    setItEditOpen(true);
  };
  const openHrComplete = () => {
    setHrForm({
      invoice_url: "",
      actual_price: request.unit_price != null ? String(request.unit_price) : "",
      note: "",
      file: null,
      mode: "file",
    });
    setModalErrors([]);
    setHrOpen(true);
  };

  return (
    <div className="sat-detail">
      <div className="sat-detail__main">
        <button type="button" className="sat-btn sat-btn--ghost" onClick={() => navigate(-1)}>
          ← {t("satList.back")}
        </button>
        {actionError && (
          <div className="sat-banner" role="alert">
            <span>
              {t("satList.actionFailed")}: {actionError}
            </span>
            <button type="button" onClick={() => setActionError("")} aria-label={t("satList.dismiss")}>
              ×
            </button>
          </div>
        )}
        <div className="sat-title-row">
          <h2>{request.title}</h2>
          {request.request_code && (
            <span className="sat-code-chip" title={t("satRequest.fields.code")}>
              {request.request_code}
            </span>
          )}
          <StatusBadge status={request.status} />
          <PriorityBadge priority={request.priority} />
        </div>

        <RequestTimeline status={request.status} needsItReview={request.needs_it_review} />

        <dl className="sat-detail__meta">
          <dt>{t("satRequest.fields.category")}</dt>
          <dd>{t(`satRequest.category.${request.category}`)}</dd>
          {request.item_url && (
            <>
              <dt>{t("satRequest.fields.itemUrl")}</dt>
              <dd>
                <a
                  href={request.item_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sat-link"
                >
                  {request.item_url}
                </a>
              </dd>
            </>
          )}
          {request.proforma_url && (
            <>
              <dt>{t("satRequest.proformaFile")}</dt>
              <dd>
                <a
                  href={request.proforma_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sat-link"
                >
                  {t("satRequest.proformaDownload")}
                </a>
              </dd>
            </>
          )}
          <dt>{t("satRequest.fields.quantity")}</dt>
          <dd>{request.quantity}</dd>
          <dt>{t("satRequest.fields.unitPrice")}</dt>
          <dd>
            {Number(request.unit_price).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}{" "}
            TL
          </dd>
          <dt>{t("satRequest.fields.totalPrice")}</dt>
          <dd>
            <strong>
              {total.toLocaleString(lang === "tr" ? "tr-TR" : "en-US")} TL
            </strong>
          </dd>
        </dl>

        <div className="sat-desc-block">
          <h3 className="sat-hint sat-hint--lg">
            {t("satRequest.fields.description")}
          </h3>
          <div className="sat-description">{request.description}</div>
        </div>

        {request.supervisor_note && (
          <div className="sat-note sat-note--supervisor">
            <strong>Birim Amiri — {t("satRequest.fields.note")}:</strong> {request.supervisor_note}
          </div>
        )}
        {request.it_note && (
          <div className="sat-note sat-note--it">
            <strong>IT — {t("satRequest.fields.note")}:</strong> {request.it_note}
          </div>
        )}
        {request.manager_note && (
          <div className="sat-note sat-note--manager">
            <strong>Yönetici — {t("satRequest.fields.note")}:</strong> {request.manager_note}
          </div>
        )}
        {request.hr_note && (
          <div className="sat-note sat-note--hr">
            <strong>IK — {t("satRequest.fields.note")}:</strong> {request.hr_note}
          </div>
        )}
        {request.rejection_reason && (
          <div className="sat-note sat-note--reject">
            <strong>{t("satRequest.fields.reason")}:</strong> {request.rejection_reason}
          </div>
        )}

        {(request.invoice_url || request.actual_price != null) && (
          <div className="sat-panel">
            <div className="sat-panel__title">
              {t("satRequest.fields.invoiceUrl")} & {t("satRequest.fields.actualPrice")}
            </div>
            {request.invoice_url && (
              <div style={{ marginBottom: 4 }}>
                <a
                  href={request.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sat-link"
                >
                  {request.invoice_url}
                </a>
              </div>
            )}
            {request.actual_price != null && (
              <div style={{ fontSize: "0.9rem" }}>
                {t("satRequest.fields.actualPrice")}:{" "}
                <strong>
                  {Number(request.actual_price).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}{" "}
                  TL
                </strong>
                {" · "}
                {t("satRequest.fields.actualTotal")}:{" "}
                <strong>
                  {(Number(request.actual_price) * Number(request.quantity)).toLocaleString(
                    lang === "tr" ? "tr-TR" : "en-US"
                  )}{" "}
                  TL
                </strong>
                {request.unit_price != null && (
                  <span className="sat-hint sat-plan">
                    (plan:{" "}
                    {(Number(request.unit_price) * Number(request.quantity)).toLocaleString(
                      lang === "tr" ? "tr-TR" : "en-US"
                    )}{" "}
                    TL)
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {canUploadDocs && (
          <div className="sat-panel">
            <div className="sat-panel__title">
              {t("satRequest.docs.title")}
            </div>
            <div className="sat-doc-uploads">
              <label>
                <span>
                  {t("satRequest.docs.proforma")}{" "}
                  {request.proforma_url ? <span className="sat-ok">✓</span> : null}
                </span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={busy}
                  onChange={(e) => {
                    onUploadDoc("proforma", e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
              <label>
                <span>
                  {t("satRequest.docs.invoice")}{" "}
                  {request.invoice_url ? <span className="sat-ok">✓</span> : null}
                </span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={busy}
                  onChange={(e) => {
                    onUploadDoc("invoice", e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <div className="sat-hint">
              {t("satRequest.docs.hint")}
            </div>
          </div>
        )}

        <div className="sat-actions">
          {canSupervisorAction && (
            <>
              <button
                className="sat-btn-success"
                disabled={busy}
                onClick={() =>
                  handle(() => satRequestService.supervisorApprove(id, null))
                }
              >
                {t("satRequest.actions.supervisorApprove")}
              </button>
              <button
                className="sat-btn-danger"
                disabled={busy}
                onClick={() => {
                  setRejectReason("");
                  setModalErrors([]);
                  setRejectOpen("supervisor");
                }}
              >
                {t("satRequest.actions.supervisorReject")}
              </button>
            </>
          )}
          {canItAction && (
            <>
              <button className="sat-btn-primary" disabled={busy} onClick={openItEdit}>
                {t("satRequest.actions.itApprove")}
              </button>
              <button
                className="sat-btn-danger"
                disabled={busy}
                onClick={() => {
                  setRejectReason("");
                  setModalErrors([]);
                  setRejectOpen("it");
                }}
              >
                {t("satRequest.actions.itReject")}
              </button>
            </>
          )}
          {canManagerAction && (
            <>
              <button
                className="sat-btn-success"
                disabled={busy}
                onClick={() => handle(() => satRequestService.managerApprove(id, null))}
              >
                {t("satRequest.actions.managerApprove")}
              </button>
              <button
                className="sat-btn-danger"
                disabled={busy}
                onClick={() => {
                  setRejectReason("");
                  setModalErrors([]);
                  setRejectOpen("manager");
                }}
              >
                {t("satRequest.actions.managerReject")}
              </button>
            </>
          )}
          {canHrAction && (
            <>
              <button
                className="sat-btn-success"
                disabled={busy}
                onClick={openHrComplete}
              >
                {t("satRequest.actions.hrComplete")}
              </button>
              <button
                className="sat-btn-danger"
                disabled={busy}
                onClick={() => {
                  setRejectReason("");
                  setModalErrors([]);
                  setRejectOpen("hr");
                }}
              >
                {t("satRequest.actions.hrReject")}
              </button>
            </>
          )}
          {canCancel && (
            <button
              className="sat-btn-danger"
              disabled={busy}
              onClick={() =>
                handle(
                  () => satRequestService.cancel(id),
                  t("satRequest.confirmCancel")
                )
              }
            >
              {t("satRequest.actions.cancel")}
            </button>
          )}
        </div>
      </div>

      <aside className="sat-detail__aside">
        <h3>{t("satRequest.fields.createdBy")}</h3>
        <div className="sat-aside-block">
          {request.created_by_name} {request.created_by_surname}
          <div className="sat-hint">{request.created_by_email}</div>
          <div className="sat-hint">
            {formatDate(request.created_at, lang)}
          </div>
        </div>
        {request.supervisor_name && (
          <>
            <h3>Birim Amiri</h3>
            <div className="sat-aside-block">
              {request.supervisor_name} {request.supervisor_surname}
            </div>
          </>
        )}

        <h3>Zaman çizelgesi</h3>
        <ul className="sat-activity">
          {activities.map((a) => (
            <li key={a.id}>
              <div>{t(`satRequest.timeline.${a.action}`) || a.action}</div>
              <div className="who">
                {a.by_name || a.by_surname
                  ? `${a.by_name || ""} ${a.by_surname || ""}`
                  : "—"}{" "}
                · {formatDate(a.created_at, lang)}
              </div>
              {a.note && (
                <div className="sat-activity__note">{a.note}</div>
              )}
            </li>
          ))}
        </ul>
      </aside>

      {/* IT Edit + Approve modal */}
      {itEditOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="sat-modal-backdrop"
          onClick={() => !busy && setItEditOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await satRequestService.itApprove(id, {
                  note: itEditForm.note || null,
                  item_url: itEditForm.item_url || undefined,
                  quantity: itEditForm.quantity
                    ? Number(itEditForm.quantity)
                    : undefined,
                  unit_price: itEditForm.unit_price
                    ? Number(itEditForm.unit_price)
                    : undefined,
                });
                setItEditOpen(false);
                await load();
              } catch (err) {
                setModalErrors([
                  err.response?.data?.msg ||
                    err.response?.data?.errors?.[0]?.msg ||
                    err.message,
                ]);
              } finally {
                setBusy(false);
              }
            }}
            className="sat-modal sat-modal--md"
          >
            <h3 className="sat-modal__title">
              {t("satRequest.actions.itApprove")}
            </h3>
            <div className="sat-hint sat-hint--lg sat-modal__hint">
              Talep alanlarını gerektiği gibi düzenleyebilirsiniz. Değişiklik
              yapmazsanız mevcut değerler korunur.
            </div>
            <div className="sat-form-grid">
              <label className="full">
                {t("satRequest.fields.itemUrl")}
                <input
                  type="url"
                  value={itEditForm.item_url}
                  onChange={(e) =>
                    setItEditForm({ ...itEditForm, item_url: e.target.value })
                  }
                />
              </label>
              <label>
                {t("satRequest.fields.quantity")}
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={itEditForm.quantity}
                  onChange={(e) =>
                    setItEditForm({ ...itEditForm, quantity: e.target.value })
                  }
                />
              </label>
              <label>
                {t("satRequest.fields.unitPrice")}
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={itEditForm.unit_price}
                  onChange={(e) =>
                    setItEditForm({ ...itEditForm, unit_price: e.target.value })
                  }
                />
              </label>
              <label className="full">
                {t("satRequest.fields.note")}
                <textarea
                  value={itEditForm.note}
                  onChange={(e) =>
                    setItEditForm({ ...itEditForm, note: e.target.value })
                  }
                  placeholder={t("satRequest.fields.notePlaceholder")}
                />
              </label>
            </div>
            {modalErrors.length > 0 && (
              <ul className="sat-form-errors" role="alert">
                {modalErrors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            <div className="sat-modal__actions">
              <button
                type="button"
                onClick={() => setItEditOpen(false)}
                disabled={busy}
                className="sat-btn"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="sat-btn sat-btn--primary"
              >
                {busy ? t("common.loading") : t("satRequest.actions.itApprove")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* HR complete modal */}
      {hrOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="sat-modal-backdrop"
          onClick={() => !busy && setHrOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={async (e) => {
              e.preventDefault();
              const price = Number(hrForm.actual_price);
              const errs = [];
              if (hrForm.mode === "url") {
                const url = String(hrForm.invoice_url || "").trim();
                if (!/^https?:\/\/\S+/i.test(url)) {
                  errs.push(t("satRequest.validation.invoiceUrlInvalid"));
                }
              } else {
                if (!hrForm.file) errs.push(t("satRequest.validation.fileRequired"));
                else if (hrForm.file.size > 10 * 1024 * 1024)
                  errs.push(t("satRequest.validation.fileTooLarge"));
              }
              if (!Number.isFinite(price) || price < 0) {
                errs.push(t("satRequest.validation.actualPriceMin"));
              }
              if (errs.length) {
                setModalErrors(errs);
                return;
              }
              setModalErrors([]);
              setBusy(true);
              try {
                if (hrForm.mode === "file") {
                  await satRequestService.hrComplete(id, {
                    actual_price: price,
                    file: hrForm.file,
                    note: hrForm.note || null,
                  });
                } else {
                  await satRequestService.hrComplete(id, {
                    invoice_url: String(hrForm.invoice_url).trim(),
                    actual_price: price,
                    note: hrForm.note || null,
                  });
                }
                setHrOpen(false);
                setHrForm({
                  invoice_url: "",
                  actual_price: "",
                  note: "",
                  file: null,
                  mode: "file",
                });
                await load();
              } catch (err) {
                setModalErrors([
                  err.response?.data?.msg ||
                    err.response?.data?.errors?.[0]?.msg ||
                    err.message,
                ]);
              } finally {
                setBusy(false);
              }
            }}
            className="sat-modal sat-modal--sm"
          >
            <h3 className="sat-modal__title">
              {t("satRequest.completeModal.title")}
            </h3>
            <div className="sat-hint sat-hint--lg sat-modal__hint">
              {t("satRequest.completeModal.hint")}
            </div>
            <div className="sat-form-grid">
              <div className="full sat-radio-row">
                <label className="sat-check">
                  <input
                    type="radio"
                    name="sat-invoice-mode"
                    checked={hrForm.mode === "file"}
                    onChange={() => setHrForm({ ...hrForm, mode: "file" })}
                  />
                  {t("satRequest.fields.invoiceFile")}
                </label>
                <label className="sat-check">
                  <input
                    type="radio"
                    name="sat-invoice-mode"
                    checked={hrForm.mode === "url"}
                    onChange={() => setHrForm({ ...hrForm, mode: "url" })}
                  />
                  {t("satRequest.fields.invoiceUrl")}
                </label>
              </div>
              {hrForm.mode === "url" ? (
                <label className="full">
                  {t("satRequest.fields.invoiceUrl")}
                  <input
                    type="url"
                    placeholder={t("satRequest.fields.invoiceUrlPlaceholder")}
                    value={hrForm.invoice_url}
                    onChange={(e) =>
                      setHrForm({ ...hrForm, invoice_url: e.target.value })
                    }
                  />
                </label>
              ) : (
                <label className="full">
                  {t("satRequest.fields.invoiceFile")}{" "}
                  <span className="sat-hint">
                    (PDF / JPG / PNG, max 10MB)
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    onChange={(e) =>
                      setHrForm({ ...hrForm, file: e.target.files?.[0] || null })
                    }
                  />
                  {hrForm.file && (
                    <div className="sat-hint">
                      {hrForm.file.name} — {(hrForm.file.size / 1024).toFixed(0)} KB
                    </div>
                  )}
                </label>
              )}
              <label>
                {t("satRequest.fields.actualPrice")}
                <input
                  type="number"
                  required
                  min={0}
                  step="0.01"
                  value={hrForm.actual_price}
                  onChange={(e) =>
                    setHrForm({ ...hrForm, actual_price: e.target.value })
                  }
                />
              </label>
              <label className="full">
                {t("satRequest.fields.note")}
                <textarea
                  value={hrForm.note}
                  onChange={(e) => setHrForm({ ...hrForm, note: e.target.value })}
                  placeholder={t("satRequest.fields.notePlaceholder")}
                />
              </label>
              <div className="full sat-hint sat-hint--lg">
                {t("satRequest.fields.actualTotal")}:{" "}
                <strong>
                  {(() => {
                    const p = Number(hrForm.actual_price);
                    const q = Number(request.quantity);
                    if (!Number.isFinite(p) || !Number.isFinite(q)) return "0";
                    return (p * q).toLocaleString(lang === "tr" ? "tr-TR" : "en-US");
                  })()}{" "}
                  TL
                </strong>
              </div>
            </div>
            {modalErrors.length > 0 && (
              <ul className="sat-form-errors" role="alert">
                {modalErrors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            <div className="sat-modal__actions">
              <button
                type="button"
                onClick={() => setHrOpen(false)}
                disabled={busy}
                className="sat-btn"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="sat-btn sat-btn--success"
              >
                {busy ? t("common.loading") : t("satRequest.actions.hrComplete")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reject reason modal (supervisor / it / manager / hr) */}
      {rejectOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="sat-modal-backdrop"
          onClick={() => !busy && setRejectOpen(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="sat-modal sat-modal--sm"
          >
            <h3 className="sat-modal__title">{t("satRequest.fields.reason")}</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("satRequest.fields.reasonPlaceholder")}
              className="sat-reject-text"
              autoFocus
            />
            {modalErrors.length > 0 && (
              <ul className="sat-form-errors" role="alert">
                {modalErrors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            <div className="sat-modal__actions">
              <button
                type="button"
                onClick={() => {
                  setRejectOpen(null);
                  setRejectReason("");
                }}
                disabled={busy}
                className="sat-btn"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={submitReject}
                className="sat-btn sat-btn--danger"
              >
                {t(
                  `satRequest.actions.${
                    rejectOpen === "supervisor"
                      ? "supervisorReject"
                      : rejectOpen === "it"
                      ? "itReject"
                      : rejectOpen === "manager"
                      ? "managerReject"
                      : "hrReject"
                  }`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SATRequestDetailPage;
